import {
  coreAlias,
  valuationDefinition,
  type ValuationCountry,
} from "./valuation";
import type { QuestionnaireDefinition, QuestionnaireField } from "./schema";

export const SYSTEM_VALUATION_ID = "system-business-valuation";

/** Fixed business inputs and contacts surround independently editable extra questions. */
export function systemValuationDefinition(
  country: ValuationCountry,
  questions?: QuestionnaireDefinition,
  legacy = false,
): QuestionnaireDefinition {
  const core = valuationDefinition(country, undefined, legacy);
  const countryField = core.steps
    .flatMap((step) => step.fields)
    .find((field) => field.id === "country");
  if (countryField?.kind === "enum")
    countryField.options = countryField.options.filter(
      (option) => option.id === country,
    );
  const email = core.steps.find((step) => step.id === "email");
  const emailField = email?.fields[0];
  if (!email || !emailField)
    throw new Error("The valuation email question is missing.");
  const contact = (id: string, label: string): QuestionnaireField => ({
    id,
    binding: id,
    label,
    kind: "text" as const,
    required: true,
    sensitivity: "standard",
    minLength: 2,
    maxLength: 200,
    source: { system: "valuation", reference: `contact.${id}` },
  });
  const fullName = contact("fullName", "What should we call you?");
  const phone: QuestionnaireField = {
    ...contact("phone", "What’s the best number to reach you?"),
    kind: "phone",
  };
  return {
    ...core,
    id: SYSTEM_VALUATION_ID,
    version: legacy ? "2" : "3",
    steps: [
      ...core.steps.filter((step) => step.id !== "email"),
      ...(questions?.steps
        .map((step) => ({
          ...step,
          fields: step.fields.filter((field) => !coreAlias(field)),
        }))
        .filter((step) => step.fields.length > 0)
        .filter((step) =>
          step.fields.every(
            (field) =>
              field.kind !== "page" && field.kind !== "computed_display",
          ),
        )
        .map((step) => ({
          ...step,
          // This default describes the editor to a broker, not the seller's question.
          description:
            step.description ===
            "Optional questions you want to ask before collecting contact details."
              ? undefined
              : step.description,
        })) ?? []),
      ...(legacy
        ? [fullName, phone]
            .map((field) => ({
              id: field.id,
              title: field.label,
              fields: [field],
            }))
            .concat([
              { ...email, title: "Where should we send your valuation?" },
            ])
        : [
            {
              id: "contact",
              title: "How can we reach you?",
              description: "Add your details to see your estimate.",
              fields: [
                {
                  ...fullName,
                  kind: "text" as const,
                  label: "Your name",
                  placeholder: "What should we call you?",
                },
                {
                  ...emailField,
                  kind: "text" as const,
                  label: "Email address",
                  placeholder: "you@example.com",
                },
                { ...phone, label: "Phone number" },
              ],
            },
          ]),
    ],
  };
}
